import { useState, FormEvent } from 'react';

interface QuoteFormProps {
  variant?: 'default' | 'compact';
}

const QuoteForm = ({ variant = 'default' }: QuoteFormProps) => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    service: '',
    zipCode: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        if (value) formBody.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d5jav6v9u36dslcgh7ng', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString(),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          phone: '',
          email: '',
          service: '',
          zipCode: '',
          message: ''
        });
        setTimeout(() => setSubmitStatus('idle'), 5000);
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (variant === 'compact') {
    return (
      <div className="bg-white rounded-2xl sm:rounded-3xl p-6 sm:p-8 lg:p-10 shadow-2xl">
        <h3 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900 mb-4 sm:mb-6 text-center">
          Get Your Free Inspection
        </h3>
        <form
          id="hero-quote-form"
          data-readdy-form
          onSubmit={handleSubmit}
          className="space-y-4 sm:space-y-5"
        >
          <div>
            <label htmlFor="compact-name" className="block text-sm font-semibold text-gray-700 mb-2">
              Full Name *
            </label>
            <input
              type="text"
              id="compact-name"
              name="name"
              required
              className="w-full px-4 py-3 sm:py-3.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm sm:text-base touch-manipulation"
              placeholder="John Smith"
            />
          </div>

          <div>
            <label htmlFor="compact-email" className="block text-sm font-semibold text-gray-700 mb-2">
              Email Address *
            </label>
            <input
              type="email"
              id="compact-email"
              name="email"
              required
              className="w-full px-4 py-3 sm:py-3.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm sm:text-base touch-manipulation"
              placeholder="john@example.com"
            />
          </div>

          <div>
            <label htmlFor="compact-phone" className="block text-sm font-semibold text-gray-700 mb-2">
              Phone Number *
            </label>
            <input
              type="tel"
              id="compact-phone"
              name="phone"
              required
              className="w-full px-4 py-3 sm:py-3.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm sm:text-base touch-manipulation"
              placeholder="(479) 555-0123"
            />
          </div>

          <div>
            <label htmlFor="compact-service" className="block text-sm font-semibold text-gray-700 mb-2">
              Service Needed *
            </label>
            <select
              id="compact-service"
              name="service"
              required
              className="w-full px-4 py-3 sm:py-3.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm sm:text-base touch-manipulation appearance-none bg-white cursor-pointer"
            >
              <option value="">Select a service</option>
              <option value="Storm Damage Repair">Storm Damage Repair</option>
              <option value="Roof Inspection">Free Roof Inspection</option>
              <option value="Roof Replacement">Roof Replacement</option>
              <option value="Emergency Repair">Emergency Repair</option>
              <option value="Insurance Claim">Insurance Claim Help</option>
              <option value="Other">Other</option>
            </select>
          </div>

          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full bg-gradient-to-r from-orange-500 to-orange-600 text-white px-6 py-3.5 sm:py-4 rounded-lg font-bold text-base sm:text-lg hover:from-orange-600 hover:to-orange-700 transition-all shadow-lg disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap touch-manipulation active:scale-95 min-h-[48px]"
          >
            {isSubmitting ? 'Submitting...' : 'Get Free Quote'}
          </button>

          {submitStatus === 'success' && (
            <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg text-sm sm:text-base">
              Thank you! We'll contact you shortly.
            </div>
          )}

          {submitStatus === 'error' && (
            <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg text-sm sm:text-base">
              Something went wrong. Please try again or call us directly.
            </div>
          )}
        </form>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl sm:rounded-3xl p-6 sm:p-8 lg:p-10 shadow-2xl">
      <h3 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900 mb-2 sm:mb-3 text-center">
        Request Your Free Inspection
      </h3>
      <p className="text-sm sm:text-base text-gray-600 mb-6 sm:mb-8 text-center px-2">
        Fill out the form below and we'll connect you with a certified contractor within 24 hours
      </p>

      <form
        id="main-quote-form"
        data-readdy-form
        onSubmit={handleSubmit}
        className="space-y-4 sm:space-y-5"
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5">
          <div>
            <label htmlFor="name" className="block text-sm font-semibold text-gray-700 mb-2">
              Full Name *
            </label>
            <input
              type="text"
              id="name"
              name="name"
              required
              className="w-full px-4 py-3 sm:py-3.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm sm:text-base touch-manipulation"
              placeholder="John Smith"
            />
          </div>

          <div>
            <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
              Email Address *
            </label>
            <input
              type="email"
              id="email"
              name="email"
              required
              className="w-full px-4 py-3 sm:py-3.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm sm:text-base touch-manipulation"
              placeholder="john@example.com"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5">
          <div>
            <label htmlFor="phone" className="block text-sm font-semibold text-gray-700 mb-2">
              Phone Number *
            </label>
            <input
              type="tel"
              id="phone"
              name="phone"
              required
              className="w-full px-4 py-3 sm:py-3.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm sm:text-base touch-manipulation"
              placeholder="(479) 555-0123"
            />
          </div>

          <div>
            <label htmlFor="service" className="block text-sm font-semibold text-gray-700 mb-2">
              Service Needed *
            </label>
            <select
              id="service"
              name="service"
              required
              className="w-full px-4 py-3 sm:py-3.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm sm:text-base touch-manipulation appearance-none bg-white cursor-pointer"
            >
              <option value="">Select a service</option>
              <option value="Storm Damage Repair">Storm Damage Repair</option>
              <option value="Roof Inspection">Free Roof Inspection</option>
              <option value="Roof Replacement">Roof Replacement</option>
              <option value="Emergency Repair">Emergency Repair</option>
              <option value="Insurance Claim">Insurance Claim Help</option>
              <option value="Other">Other</option>
            </select>
          </div>
        </div>

        <div>
          <label htmlFor="address" className="block text-sm font-semibold text-gray-700 mb-2">
            Property Address
          </label>
          <input
            type="text"
            id="address"
            name="address"
            className="w-full px-4 py-3 sm:py-3.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm sm:text-base touch-manipulation"
            placeholder="123 Main St, Fayetteville, AR"
          />
        </div>

        <div>
          <label htmlFor="message" className="block text-sm font-semibold text-gray-700 mb-2">
            Additional Details
          </label>
          <textarea
            id="message"
            name="message"
            rows={4}
            maxLength={500}
            className="w-full px-4 py-3 sm:py-3.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none text-sm sm:text-base touch-manipulation"
            placeholder="Tell us about your roofing needs... (max 500 characters)"
          ></textarea>
        </div>

        <button
          type="submit"
          disabled={isSubmitting}
          className="w-full bg-gradient-to-r from-orange-500 to-orange-600 text-white px-6 py-3.5 sm:py-4 rounded-lg font-bold text-base sm:text-lg hover:from-orange-600 hover:to-orange-700 transition-all shadow-lg disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap touch-manipulation active:scale-95 min-h-[48px]"
        >
          {isSubmitting ? 'Submitting...' : 'Get Free Quote'}
        </button>

        {submitStatus === 'success' && (
          <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg text-sm sm:text-base">
            <strong>Thank you!</strong> We've received your request and will connect you with a certified contractor within 24 hours.
          </div>
        )}

        {submitStatus === 'error' && (
          <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg text-sm sm:text-base">
            <strong>Oops!</strong> Something went wrong. Please try again or call us directly at (479) 345-5312.
          </div>
        )}
      </form>
    </div>
  );
};

export default QuoteForm;